import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, { TileStatusContent } from "../../../components/Tile/TileContent";
import { useLocalFilters } from "../../../hooks/useLocalFilters";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { useOcelMetadata } from "../../../stores/OcelContext";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import LocalFiltersActiveBadge from "../../../components/common/LocalFiltersActiveBadge";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../../../components/ui/tooltip";
import { createFilteredRequest, makePostRequest } from "../../../services/apiClient";
import ObjectConversionKPISettings from "./ObjectConversionKPISettings";
import { computeConversionRatio } from "./formula";
import { resolveAmpelColor } from "../EfficiencyKPI/formula";
import {
  ALL_TIME_PRESET,
  resolveCurrentRange,
  resolvePreviousRange,
  formatRangeLabel,
  formatComparisonCaption,
  detectPeriodPresetFromDateRange,
} from "../_shared/periodPresets";
import {
  TREND_BADGE_CLASSES,
  KPIValueRow,
  KPIValueColumn,
  KPIValueSlot,
  KPITrendSlot,
  KPIBigValue,
  KPICaption,
  KPITrendBadge,
} from "../_shared/kpiTrendDisplay";

const ERROR_MESSAGES = {
  MISSING_DATA: "Daten nicht verfügbar",
  DENOMINATOR_ZERO: "Nenner ist 0 — Berechnung nicht möglich",
};

const formatPercent = (value) =>
  `${value.toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;

const formatCount = (value) =>
  value === null || value === undefined ? "–" : value.toLocaleString("de-DE");

// Trend indicator is absolute percentage points (aktueller Wert - Vorperiodenwert),
// same convention as EfficiencyKPI.jsx — both ratio values are already 0-100.
const formatTrendPP = (trend) => {
  const sign = trend.diff > 0 ? "+" : trend.diff < 0 ? "−" : "";
  return `${sign}${Math.abs(trend.diff).toFixed(1)} pp`;
};

const describeCondition = (activities, matchType) => {
  if (!activities || activities.length === 0) return "alle Objekte";
  if (activities.length === 1) return activities[0];
  return `${activities.join(", ")} (${matchType === "all" ? "UND" : "ODER"})`;
};

const ObjectConversionKPI = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    currentFilters,
    globalFilterState,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const metadata = useOcelMetadata(activeFile);

  // Component configuration
  const [componentTitle, setComponentTitle] = useState("");
  const [objectType, setObjectType] = useState("");
  const [numeratorActivities, setNumeratorActivities] = useState([]);
  const [numeratorMatchType, setNumeratorMatchType] = useState("any");
  const [denominatorActivities, setDenominatorActivities] = useState([]);
  const [denominatorMatchType, setDenominatorMatchType] = useState("any");
  const [ampelEnabled, setAmpelEnabled] = useState(false);
  const [ampelLower, setAmpelLower] = useState(50);
  const [ampelUpper, setAmpelUpper] = useState(80);
  const [ampelColorRed, setAmpelColorRed] = useState("#ef4444");
  const [ampelColorYellow, setAmpelColorYellow] = useState("#eab308");
  const [ampelColorGreen, setAmpelColorGreen] = useState("#22c55e");

  // Betrachtungszeitraum / Vorperioden-Vergleich configuration
  const [periodPreset, setPeriodPreset] = useState(ALL_TIME_PRESET.key);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [showPeriodLabel, setShowPeriodLabel] = useState(true);
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [trendDirection, setTrendDirection] = useState("higherIsBetter");

  // Data state
  const [numeratorData, setNumeratorData] = useState(null);
  const [denominatorData, setDenominatorData] = useState(null);
  const [previousNumeratorData, setPreviousNumeratorData] = useState(null);
  const [previousDenominatorData, setPreviousDenominatorData] = useState(null);
  const [resolvedRange, setResolvedRange] = useState(null);
  const [resolvedPreviousRange, setResolvedPreviousRange] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);

  // Modal
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Derived metadata — availableObjectTypes filtered by global object-type filter only
  const [availableObjectTypes, setAvailableObjectTypes] = useState([]);

  useEffect(() => {
    if (!metadata?.object_types) return;
    const allTypes = Object.keys(metadata.object_types).sort();
    const filtered =
      globalFilterState?.objectTypes?.length > 0
        ? allTypes.filter((t) => globalFilterState.objectTypes.includes(t))
        : allTypes;
    setAvailableObjectTypes(filtered);
    if (objectType && !filtered.includes(objectType)) {
      setObjectType(filtered[0] ?? "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterState?.objectTypes, metadata]);

  const availableActivities = useMemo(
    () => (metadata?.activity_types ? Object.keys(metadata.activity_types).sort() : []),
    [metadata]
  );

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.objectType !== undefined) setObjectType(initialSettings.objectType);
    if (initialSettings.numeratorActivities !== undefined) setNumeratorActivities(initialSettings.numeratorActivities);
    if (initialSettings.numeratorMatchType !== undefined) setNumeratorMatchType(initialSettings.numeratorMatchType);
    if (initialSettings.denominatorActivities !== undefined) setDenominatorActivities(initialSettings.denominatorActivities);
    if (initialSettings.denominatorMatchType !== undefined) setDenominatorMatchType(initialSettings.denominatorMatchType);
    if (initialSettings.ampelEnabled !== undefined) setAmpelEnabled(initialSettings.ampelEnabled);
    if (initialSettings.ampelLower !== undefined) setAmpelLower(initialSettings.ampelLower);
    if (initialSettings.ampelUpper !== undefined) setAmpelUpper(initialSettings.ampelUpper);
    if (initialSettings.ampelColorRed !== undefined) setAmpelColorRed(initialSettings.ampelColorRed);
    if (initialSettings.ampelColorYellow !== undefined) setAmpelColorYellow(initialSettings.ampelColorYellow);
    if (initialSettings.ampelColorGreen !== undefined) setAmpelColorGreen(initialSettings.ampelColorGreen);
    if (initialSettings.periodPreset !== undefined) {
      setPeriodPreset(initialSettings.periodPreset);
    } else {
      // Configs saved before periodPreset existed — derive a best-guess preset
      // from the legacy resolved dateRange so old tiles keep working unchanged.
      setPeriodPreset(detectPeriodPresetFromDateRange(initialSettings.localFilters?.dateRange));
    }
    if (initialSettings.customStart !== undefined) setCustomStart(initialSettings.customStart);
    if (initialSettings.customEnd !== undefined) setCustomEnd(initialSettings.customEnd);
    if (initialSettings.showPeriodLabel !== undefined) setShowPeriodLabel(initialSettings.showPeriodLabel);
    if (initialSettings.comparisonEnabled !== undefined) setComparisonEnabled(initialSettings.comparisonEnabled);
    if (initialSettings.trendDirection !== undefined) setTrendDirection(initialSettings.trendDirection);
    setPendingApply(true);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      componentTitle,
      objectType,
      numeratorActivities,
      numeratorMatchType,
      denominatorActivities,
      denominatorMatchType,
      ampelEnabled,
      ampelLower,
      ampelUpper,
      ampelColorRed,
      ampelColorYellow,
      ampelColorGreen,
      localFilters,
      periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
    }));
  }, [
    componentTitle, objectType, numeratorActivities, numeratorMatchType, denominatorActivities, denominatorMatchType,
    ampelEnabled, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen,
    localFilters,
    periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Settings modal lifecycle ───────────────────────────────────────────────
  const handleOpenSettings = useCallback(() => {
    startDraftMode();
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback(
    (val) => {
      setIsSettingsModalOpen(val);
      if (!val) cancelDraftFilters();
    },
    [cancelDraftFilters]
  );

  // ── Data fetching ──────────────────────────────────────────────────────────
  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = useCallback(async () => {
    if (!activeFile || !objectType) return;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);
    try {
      const now = new Date();
      const currentRange = resolveCurrentRange(periodPreset, { customStart, customEnd }, now);
      const previousRange = comparisonEnabled
        ? resolvePreviousRange(periodPreset, currentRange, now)
        : null;

      // A non-"all" period is itself a local override, same as any other
      // local filter — decide local-vs-global the same way useLocalFilters
      // does, but without depending on this render's (possibly stale) value.
      const usesLocalFilters = hasLocalFilters || periodPreset !== ALL_TIME_PRESET.key;
      const filterBase = usesLocalFilters ? localFilters : globalFilterState;

      const baseRequest = {
        file_type: activeFile,
        object_type: objectType || undefined,
        limit: 1,
        offset: 0,
      };

      // Zähler und Nenner sind zwei unabhängige, periodengebundene
      // Aktivitäts-Bedingungen (nicht dieselbe geteilte Objektmenge wie bei
      // EfficiencyKPI) — daher zwei getrennte /ocel/object-table-Aufrufe statt
      // eines gemeinsamen. `activityFilters` wird hier bewusst NICHT über den
      // von ActivityFilterBlock genutzten {included, excluded}-Legacy-Shape
      // gebaut, sondern direkt im Ziel-Shape {enabled, activities, match_type},
      // den createFilteredRequest unverändert als `activity_filters` durchreicht
      // (services/helpers/filterRequest.js hat dafür kein Remapping) und den
      // das Backend-ActivityFilter-Schema direkt unterstützt (AND/OR via
      // match_type — siehe backend/src/filter/engine.py::_apply_activity_filter).
      const buildRequest = (dateRange, activities, matchType) =>
        createFilteredRequest(baseRequest, {
          ...filterBase,
          dateRange,
          activityFilters: { enabled: activities.length > 0, activities, match_type: matchType },
        });

      const fetches = [
        makePostRequest("/ocel/object-table", buildRequest(currentRange, numeratorActivities, numeratorMatchType), {}, "Umwandlungsquote (Zähler)"),
        makePostRequest("/ocel/object-table", buildRequest(currentRange, denominatorActivities, denominatorMatchType), {}, "Umwandlungsquote (Nenner)"),
      ];

      if (previousRange) {
        fetches.push(
          makePostRequest("/ocel/object-table", buildRequest(previousRange, numeratorActivities, numeratorMatchType), {}, "Umwandlungsquote (Zähler, Vorperiode)"),
          makePostRequest("/ocel/object-table", buildRequest(previousRange, denominatorActivities, denominatorMatchType), {}, "Umwandlungsquote (Nenner, Vorperiode)")
        );
      }

      const results = await Promise.all(fetches);
      if (seq !== fetchSeqRef.current) return;
      setNumeratorData(results[0]);
      setDenominatorData(results[1]);
      setPreviousNumeratorData(results[2] ?? null);
      setPreviousDenominatorData(results[3] ?? null);
      setResolvedRange(currentRange);
      setResolvedPreviousRange(previousRange);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err?.message || "Failed to load conversion KPI");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [
    activeFile, objectType, numeratorActivities, numeratorMatchType, denominatorActivities, denominatorMatchType,
    periodPreset, customStart, customEnd, comparisonEnabled, hasLocalFilters, localFilters, globalFilterState,
  ]);

  // Fire after Apply commits new config + filters
  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApply]);

  // Auto-reload when filters or active file changes (after first load)
  useEffect(() => {
    if (hasFetched) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, activeFile]);

  useAutoLoad(fetchData, [activeFile, currentFilters], numeratorData);

  useEffect(() => {
    if (onRefresh) onRefresh(fetchData);
  }, [onRefresh, fetchData]);

  // A non-"all" period is a local override in its own right, but it no longer
  // lives inside localFilters.dateRange (see fetchData) — reflect it in the
  // badge here instead of writing it back into hook state.
  const periodIsLocalOverride = periodPreset !== ALL_TIME_PRESET.key;
  const displayHasLocalFilters = hasLocalFilters || periodIsLocalOverride;
  const displayLocalFilterCount = localFilterCount + (periodIsLocalOverride ? 1 : 0);

  // ── Header actions ─────────────────────────────────────────────────────────
  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {displayHasLocalFilters && (
          <LocalFiltersActiveBadge
            hasLocalFilters={displayHasLocalFilters}
            localFilterCount={displayLocalFilterCount}
            onClearLocalFilters={clearLocalFilters}
          />
        )}
        <button
          onClick={handleOpenSettings}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={t('common:actions.openSettings')}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs">{t('common:actions.settings')}</span>
        </button>
      </div>
    ),
    [handleOpenSettings, displayHasLocalFilters, displayLocalFilterCount, clearLocalFilters]
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  useEffect(() => {
    if (onTitleChange) onTitleChange(componentTitle || "Umwandlungsquote");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayHasLocalFilters, componentTitle]);

  // ── Derived display values ─────────────────────────────────────────────────
  const numeratorCount = numeratorData?.aggregations?.total_objects;
  const denominatorCount = denominatorData?.aggregations?.total_objects;

  const formulaResult = useMemo(
    () => computeConversionRatio({ numeratorCount, denominatorCount }),
    [numeratorCount, denominatorCount]
  );

  // "Gesamter Zeitraum" has no meaningful Vorperiode at all — treat comparison
  // as off regardless of the raw stored flag (same rationale as EfficiencyKPI.jsx).
  const effectiveComparisonEnabled = comparisonEnabled && periodPreset !== ALL_TIME_PRESET.key;

  const previousFormulaResult = useMemo(
    () => (effectiveComparisonEnabled && previousNumeratorData && previousDenominatorData
      ? computeConversionRatio({
          numeratorCount: previousNumeratorData?.aggregations?.total_objects,
          denominatorCount: previousDenominatorData?.aggregations?.total_objects,
        })
      : null),
    [effectiveComparisonEnabled, previousNumeratorData, previousDenominatorData]
  );

  const ampelColor = useMemo(() => {
    if (!ampelEnabled || formulaResult.error) return null;
    return resolveAmpelColor(formulaResult.value, {
      lower: ampelLower,
      upper: ampelUpper,
      colorRed: ampelColorRed,
      colorYellow: ampelColorYellow,
      colorGreen: ampelColorGreen,
    });
  }, [ampelEnabled, formulaResult, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen]);

  const ampelBadgeClass =
    ampelColor === ampelColorGreen ? TREND_BADGE_CLASSES.positive
    : ampelColor === ampelColorRed ? TREND_BADGE_CLASSES.negative
    : null;

  const dataBasisText = useMemo(() => ({
    numeratorLabel: describeCondition(numeratorActivities, numeratorMatchType),
    denominatorLabel: describeCondition(denominatorActivities, denominatorMatchType),
  }), [numeratorActivities, numeratorMatchType, denominatorActivities, denominatorMatchType]);

  // Tooltip content shared by both the normal display and the error state —
  // showing the raw Zähler-/Nenner-Objektanzahl alongside the condition that
  // produced it makes the number self-explanatory on hover, and for
  // DENOMINATOR_ZERO it directly shows *why* (Nenner: 0 Objekte).
  const countTooltip = (
    <div className="flex flex-col gap-2 py-0.5 text-left">
      <div>
        <div className="font-semibold">Zähler: {formatCount(numeratorCount)} Objekte</div>
        <div className="text-white/70 text-xs">{dataBasisText.numeratorLabel}</div>
      </div>
      <div className="w-full h-px bg-white/40" />
      <div>
        <div className="font-semibold">Nenner: {formatCount(denominatorCount)} Objekte</div>
        <div className="text-white/70 text-xs">{dataBasisText.denominatorLabel}</div>
      </div>
    </div>
  );

  const displayTitle = componentTitle || "Umwandlungsquote";

  const hasFormulaConfigured = !!objectType && numeratorActivities.length > 0;

  const periodLabel = showPeriodLabel && resolvedRange
    ? formatRangeLabel(resolvedRange)
    : null;

  const previousPeriodLabel = resolvedPreviousRange
    ? formatRangeLabel(resolvedPreviousRange)
    : null;

  const comparisonCaption = previousPeriodLabel
    ? formatComparisonCaption(periodPreset, previousPeriodLabel)
    : null;

  const trend = useMemo(() => {
    if (!effectiveComparisonEnabled || formulaResult.error || !previousFormulaResult || previousFormulaResult.error) {
      return null;
    }
    const diff = formulaResult.value - previousFormulaResult.value;
    const direction = diff > 0 ? "up" : diff < 0 ? "down" : "flat";
    const improved = trendDirection === "higherIsBetter" ? diff > 0 : diff < 0;
    const worsened = trendDirection === "higherIsBetter" ? diff < 0 : diff > 0;
    const color = diff === 0 ? "neutral" : improved ? "positive" : worsened ? "negative" : "neutral";
    return { diff, direction, color };
  }, [effectiveComparisonEnabled, formulaResult, previousFormulaResult, trendDirection]);

  return (
    <BaseTile loading={loading} error={error} minHeight="sm" padding="sm">
      {/* Placeholder when not yet configured */}
      {(!hasFetched || !hasFormulaConfigured) && !loading && !error && (
        <TileStatusContent
          status="info"
          title="Umwandlungsquote konfigurieren"
          message="Klicken Sie auf das Zahnrad, um Objekttyp und Zähler-Bedingung zu konfigurieren."
          icon={
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
      )}

      {/* KPI display — same grid structure and text palette as EfficiencyKPI.jsx */}
      {hasFetched && hasFormulaConfigured && !loading && !error && numeratorData && denominatorData && (
        <TileContent align="stretch" justify="start">
          <div className={`w-full flex flex-col gap-1 py-1 ${!effectiveComparisonEnabled ? "items-center" : ""}`}>
            <TooltipProvider>
              <div className="text-sm font-medium text-gray-700">
                {displayTitle}
              </div>

              {formulaResult.error ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-sm font-medium text-gray-500 cursor-default inline-block">
                      {ERROR_MESSAGES[formulaResult.error]}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{countTooltip}</TooltipContent>
                </Tooltip>
              ) : (
                <KPIValueRow>
                  <KPIValueColumn>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <KPIValueSlot className="cursor-default">
                          <KPIBigValue
                            className={ampelColor ? `rounded-lg px-3 py-0.5 ${ampelBadgeClass || ""}` : undefined}
                            style={ampelColor && !ampelBadgeClass ? { backgroundColor: `${ampelColor}26`, color: ampelColor } : undefined}
                          >
                            {formatPercent(formulaResult.value)}
                          </KPIBigValue>
                        </KPIValueSlot>
                      </TooltipTrigger>
                      <TooltipContent>{countTooltip}</TooltipContent>
                    </Tooltip>
                    {periodLabel && <KPICaption>{periodLabel}</KPICaption>}
                  </KPIValueColumn>

                  {effectiveComparisonEnabled && (
                    <KPIValueColumn>
                      {trend ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <KPITrendSlot className="cursor-default">
                              <KPITrendBadge trend={trend}>{formatTrendPP(trend)}</KPITrendBadge>
                            </KPITrendSlot>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div>Aktuell: {formatPercent(formulaResult.value)}</div>
                            <div>Vorperiode: {formatPercent(previousFormulaResult.value)}</div>
                            {previousPeriodLabel && <div>{previousPeriodLabel}</div>}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <KPITrendSlot>
                          <KPICaption>Kein Vergleichswert verfügbar</KPICaption>
                        </KPITrendSlot>
                      )}

                      {trend && comparisonCaption && <KPICaption>{comparisonCaption}</KPICaption>}
                    </KPIValueColumn>
                  )}
                </KPIValueRow>
              )}
            </TooltipProvider>
          </div>
        </TileContent>
      )}

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        title="Umwandlungsquote Settings"
        getInitialValues={() => ({
          componentTitle, objectType,
          numeratorActivities, numeratorMatchType, denominatorActivities, denominatorMatchType,
          ampelEnabled, ampelLower, ampelUpper, ampelColorRed, ampelColorYellow, ampelColorGreen,
          periodPreset, customStart, customEnd, showPeriodLabel, comparisonEnabled, trendDirection,
        })}
        onApply={(values) => {
          setComponentTitle(values.componentTitle);
          setObjectType(values.objectType);
          setNumeratorActivities(values.numeratorActivities);
          setNumeratorMatchType(values.numeratorMatchType);
          setDenominatorActivities(values.denominatorActivities);
          setDenominatorMatchType(values.denominatorMatchType);
          setAmpelEnabled(values.ampelEnabled);
          setAmpelLower(values.ampelLower);
          setAmpelUpper(values.ampelUpper);
          setAmpelColorRed(values.ampelColorRed);
          setAmpelColorYellow(values.ampelColorYellow);
          setAmpelColorGreen(values.ampelColorGreen);
          setPeriodPreset(values.periodPreset);
          setCustomStart(values.customStart);
          setCustomEnd(values.customEnd);
          setShowPeriodLabel(values.showPeriodLabel);
          setComparisonEnabled(values.comparisonEnabled);
          setTrendDirection(values.trendDirection);
          if (onTitleChange && values.componentTitle) {
            onTitleChange(values.componentTitle);
          }
          applyDraftFilters();
          setPendingApply(true);
        }}
        applyButtonText="Apply & Load"
        isApplyDisabled={false}
      >
        {({ values, setField }) => (
          <ObjectConversionKPISettings
            componentTitle={values.componentTitle}
            onTitleChange={(v) => setField("componentTitle", v)}
            objectType={values.objectType}
            onObjectTypeChange={(v) => setField("objectType", v)}
            numeratorActivities={values.numeratorActivities}
            onNumeratorActivitiesChange={(v) => setField("numeratorActivities", v)}
            numeratorMatchType={values.numeratorMatchType}
            onNumeratorMatchTypeChange={(v) => setField("numeratorMatchType", v)}
            denominatorActivities={values.denominatorActivities}
            onDenominatorActivitiesChange={(v) => setField("denominatorActivities", v)}
            denominatorMatchType={values.denominatorMatchType}
            onDenominatorMatchTypeChange={(v) => setField("denominatorMatchType", v)}
            ampelEnabled={values.ampelEnabled}
            ampelLower={values.ampelLower}
            ampelUpper={values.ampelUpper}
            ampelColorRed={values.ampelColorRed}
            ampelColorYellow={values.ampelColorYellow}
            ampelColorGreen={values.ampelColorGreen}
            onAmpelEnabledChange={(v) => setField("ampelEnabled", v)}
            onAmpelLowerChange={(v) => setField("ampelLower", v)}
            onAmpelUpperChange={(v) => setField("ampelUpper", v)}
            onAmpelColorRedChange={(v) => setField("ampelColorRed", v)}
            onAmpelColorYellowChange={(v) => setField("ampelColorYellow", v)}
            onAmpelColorGreenChange={(v) => setField("ampelColorGreen", v)}
            availableObjectTypes={availableObjectTypes}
            availableActivities={availableActivities}
            localFilters={localFilters}
            updateLocalFilter={updateLocalFilter}
            activeFile={activeFile}
            loading={loading}
            periodPreset={values.periodPreset}
            onPeriodPresetChange={(v) => setField("periodPreset", v)}
            customStart={values.customStart}
            onCustomStartChange={(v) => setField("customStart", v)}
            customEnd={values.customEnd}
            onCustomEndChange={(v) => setField("customEnd", v)}
            showPeriodLabel={values.showPeriodLabel}
            onShowPeriodLabelChange={(v) => setField("showPeriodLabel", v)}
            comparisonEnabled={values.comparisonEnabled}
            onComparisonEnabledChange={(v) => setField("comparisonEnabled", v)}
            trendDirection={values.trendDirection}
            onTrendDirectionChange={(v) => setField("trendDirection", v)}
          />
        )}
      </TileSettingsModal>
    </BaseTile>
  );
};

export default ObjectConversionKPI;
